// Parser for /counterparties URL search params. Canonical entity name is
// "counterparty" everywhere (CONTEXT.md). The router hands us an unknown
// search record; we narrow it to a known shape, drop unknown values, and
// fall back to safe defaults. Pure function — tested in isolation.

export const COUNTERPARTY_TYPE_VALUES = [
  'vendor',
  'employee',
  'client',
  'authority',
  'self',
] as const;

export type CounterpartyType = (typeof COUNTERPARTY_TYPE_VALUES)[number];

export const COUNTERPARTY_SORT_VALUES = [
  'name-asc',
  'name-desc',
  'transactions-desc',
  'aliases-desc',
] as const;

export type CounterpartySort = (typeof COUNTERPARTY_SORT_VALUES)[number];

export interface CounterpartiesSearch {
  readonly type: CounterpartyType | undefined;
  readonly sort: CounterpartySort;
  readonly q: string | undefined;
  readonly selected: string | undefined;
}

const TYPE_SET: ReadonlySet<string> = new Set(COUNTERPARTY_TYPE_VALUES);
const SORT_SET: ReadonlySet<string> = new Set(COUNTERPARTY_SORT_VALUES);

function nonEmptyString(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

export function parseCounterpartiesSearch(input: Record<string, unknown>): CounterpartiesSearch {
  const type =
    typeof input.type === 'string' && TYPE_SET.has(input.type)
      ? (input.type as CounterpartyType)
      : undefined;
  const sort =
    typeof input.sort === 'string' && SORT_SET.has(input.sort)
      ? (input.sort as CounterpartySort)
      : 'name-asc';
  return {
    type,
    sort,
    q: nonEmptyString(input.q),
    selected: nonEmptyString(input.selected),
  };
}
